// Reporting's Download (owner, 2026-11-03: "a download button for exporting the
// pdf in view ... a pop-up modal could offer pdf vs Excel formats, as well as an
// option to include pdfs of the underlying invoices").
//
// WHAT EACH CHOICE PRODUCES:
//
//  - PDF: the chart on screen, drawn as labelled bars with their values, then
//    the listing of every invoice the filters match. With the invoices'
//    documents included, the existing invoice report (`reportPdf.ts`: its cover
//    listing and each invoice's own pages) is appended to the same file.
//  - Excel: one workbook, two sheets — the chart's rows as numbers, and the
//    invoice listing. A workbook cannot carry PDF pages, so with the documents
//    included a SECOND file downloads beside it: the same invoice report.
//
// Nothing is stored. The invoice report is still requested through
// `POST /reports`, so including the documents writes the same audit row the
// old Submit did; the chart and listing alone read nothing that is not already
// on screen.
import type { PDFDocument, PDFFont, PDFPage } from 'pdf-lib'
import type { BarRowData } from '../components/DashboardCharts'
import type { Invoice } from './types'
import type { ReportChart } from './reportCharts'
import { downloadUrl } from './download'
import { formatCurrency, formatDate, formatTimestampDate } from './format'
import { statusLabel } from './constants'
import { buildWorkbook, type Cell } from './xlsxWrite'

export type ExportFormat = 'pdf' | 'excel'

/** What a file needs of a report: its title and its two column headings. Every
    section's catalogue entry has them (`reportCharts.ts`, `overTimeCharts.ts`). */
type ExportableChart = Pick<ReportChart, 'title' | 'labelHeader' | 'valueHeader'>

interface ChartExportInput {
  chart: ExportableChart
  rows: readonly BarRowData[]
  /** True when the server left rows out of the read behind the chart. */
  truncated: boolean
  /** The filters, as sentences — the same lines the invoice report's cover prints. */
  criteriaLines: readonly string[]
  invoices: readonly Invoice[]
  /** The listing's full count, when it is larger than `invoices`. */
  invoiceTotal: number | null
  requestedBy: string
  at: Date
}

/** `2026-11-03 14.05` in Eastern time — a colon is illegal in a file name. */
function stamp(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}.${get('minute')}`
}

export function exportFileName(chart: ExportableChart, format: ExportFormat, at: Date): string {
  return `${chart.title} ${stamp(at)}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
}

const runOn = (at: Date) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(at)

/** Save bytes to the user's disk under `fileName`. */
export function saveBytes(bytes: Uint8Array, fileName: string, type: string): void {
  const url = URL.createObjectURL(new Blob([bytes as unknown as BlobPart], { type }))
  try {
    downloadUrl(url, fileName)
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

// ---- Excel ---------------------------------------------------------------------

const LISTING_HEADER = ['Company', 'Vendor', 'Invoice #', 'Invoice Date', 'Uploaded Date', 'Exported Date', 'Status', 'Total']

function listingCells(inv: Invoice): Cell[] {
  return [
    inv.company_name ?? '',
    inv.vendor_name ?? '',
    inv.external_invoice_id ?? '',
    inv.invoice_date ? formatDate(inv.invoice_date) : '',
    formatTimestampDate(inv.uploaded_at),
    formatTimestampDate(inv.exported_at),
    statusLabel(inv.status),
    inv.total === null ? null : { money: inv.total },
  ]
}

/** Spend charts' values are dollars; every other chart's are counts, days or percents. */
function isMoneyChart(chart: ExportableChart): boolean {
  return chart.valueHeader === 'Spend'
}

export function buildChartWorkbook(input: ChartExportInput): Uint8Array {
  const preamble = [
    input.chart.title,
    `Run by ${input.requestedBy} on ${runOn(input.at)} (Eastern)`,
    ...(input.criteriaLines.length > 0 ? input.criteriaLines : ['Filters: none']),
    ...(input.truncated ? ['Some rows with small numbers on every measure are not shown.'] : []),
  ]
  const money = isMoneyChart(input.chart)
  return buildWorkbook([
    {
      name: input.chart.title,
      preamble,
      header: [input.chart.labelHeader, input.chart.valueHeader],
      rows: input.rows.map((row) => [row.label, money ? { money: row.total } : row.total]),
      widths: [48, 22],
    },
    {
      name: 'Invoices',
      preamble: [
        'Invoices Included in This View',
        ...(input.invoiceTotal !== null && input.invoiceTotal > input.invoices.length
          ? [`The first ${input.invoices.length} of ${input.invoiceTotal} invoices.`]
          : []),
      ],
      header: LISTING_HEADER,
      rows: input.invoices.map(listingCells),
      widths: [28, 32, 16, 14, 14, 14, 24, 14],
    },
  ])
}

// ---- PDF -----------------------------------------------------------------------

const PAGE_W = 612
const PAGE_H = 792
const MARGIN = 40
const INK = { r: 0.14, g: 0.14, b: 0.14 }
const MUTED = { r: 0.42, g: 0.42, b: 0.42 }
const RULE = { r: 0.85, g: 0.85, b: 0.85 }

/**
 * Text the standard fonts can draw. They encode WinAnsi only and THROW on
 * anything else, so a vendor name in another script would stop the export;
 * those characters print as `?` instead.
 */
function ansi(text: string): string {
  return text.replace(/[^\u0020-\u007E\u00A0-\u00FF\u2013\u2014\u2018\u2019\u201C\u201D\u2022\u2026]/g, '?')
}

/** Cut `text` with an ellipsis to fit `width` points. */
function fit(text: string, font: PDFFont, size: number, width: number): string {
  const clean = ansi(text)
  if (font.widthOfTextAtSize(clean, size) <= width) return clean
  let lo = 0
  let hi = clean.length
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (font.widthOfTextAtSize(`${clean.slice(0, mid)}…`, size) <= width) lo = mid
    else hi = mid - 1
  }
  return `${clean.slice(0, lo)}…`
}

function hex(color: string): { r: number; g: number; b: number } {
  const n = Number.parseInt(color.replace('#', ''), 16)
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 }
}

/** Draws page after page top-down, starting a new one when the next row will not fit. */
class Writer {
  page!: PDFPage
  y = 0
  private doc: PDFDocument
  private rgb: typeof import('pdf-lib').rgb
  constructor(doc: PDFDocument, rgb: typeof import('pdf-lib').rgb) {
    this.doc = doc
    this.rgb = rgb
    this.newPage()
  }
  newPage(): void {
    this.page = this.doc.addPage([PAGE_W, PAGE_H])
    this.y = PAGE_H - MARGIN
  }
  /** Room for `height` more points, or a new page. Returns true when it turned. */
  need(height: number): boolean {
    if (this.y - height < MARGIN + 18) {
      this.newPage()
      return true
    }
    return false
  }
  text(value: string, x: number, size: number, font: PDFFont, color = INK, width = PAGE_W - MARGIN - x): void {
    this.page.drawText(fit(value, font, size, width), { x, y: this.y, size, font, color: this.rgb(color.r, color.g, color.b) })
  }
  rule(): void {
    this.page.drawLine({
      start: { x: MARGIN, y: this.y },
      end: { x: PAGE_W - MARGIN, y: this.y },
      thickness: 0.5,
      color: this.rgb(RULE.r, RULE.g, RULE.b),
    })
  }
}

export async function buildChartPdf(input: ChartExportInput): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib')
  const doc = await PDFDocument.create()
  doc.setTitle(ansi(input.chart.title))
  const body = await doc.embedFont(StandardFonts.Helvetica)
  const bold = await doc.embedFont(StandardFonts.HelveticaBold)
  const w = new Writer(doc, rgb)

  // -- Header: title, who and when, the filters --------------------------------
  w.y -= 6
  w.text(input.chart.title, MARGIN, 18, bold)
  w.y -= 16
  w.text(`Run by ${input.requestedBy} on ${runOn(input.at)} (Eastern)`, MARGIN, 9, body, MUTED)
  w.y -= 18
  w.text('Filters', MARGIN, 10, bold)
  w.y -= 13
  for (const line of input.criteriaLines.length > 0 ? input.criteriaLines : ['None. Every invoice you may see is included.']) {
    w.need(12)
    w.text(line, MARGIN + 8, 9, body)
    w.y -= 12
  }
  if (input.truncated) {
    w.text('Some rows with small numbers on every measure are not shown.', MARGIN + 8, 9, body, MUTED)
    w.y -= 12
  }
  w.y -= 8

  // -- The chart: label | bar | value ------------------------------------------
  const labelW = 170
  const valueW = 80
  const barX = MARGIN + labelW + 8
  const barMax = PAGE_W - MARGIN - valueW - 8 - barX
  const max = Math.max(...input.rows.map((row) => row.total), 1)
  const rowH = 15
  const chartHead = () => {
    w.text(input.chart.labelHeader, MARGIN, 9, bold, MUTED, labelW)
    w.page.drawText(input.chart.valueHeader, {
      x: PAGE_W - MARGIN - bold.widthOfTextAtSize(ansi(input.chart.valueHeader), 9),
      y: w.y,
      size: 9,
      font: bold,
      color: rgb(MUTED.r, MUTED.g, MUTED.b),
    })
    w.y -= 6
    w.rule()
    w.y -= rowH - 3
  }
  w.need(40)
  chartHead()
  if (input.rows.length === 0) {
    w.text('Nothing to show for these filters.', MARGIN, 9, body, MUTED)
    w.y -= rowH
  }
  for (const row of input.rows) {
    if (w.need(rowH)) chartHead()
    w.text(row.label, MARGIN, 9, body, INK, labelW)
    const length = Math.max((row.total / max) * barMax, row.total > 0 ? 2 : 0)
    let x = barX
    const sum = row.segments.reduce((acc, seg) => acc + Math.max(seg.value, 0), 0) || 1
    for (const seg of row.segments) {
      if (seg.value <= 0) continue
      const segW = (seg.value / sum) * length
      const c = hex(seg.color)
      w.page.drawRectangle({ x, y: w.y - 1, width: segW, height: 9, color: rgb(c.r, c.g, c.b) })
      x += segW
    }
    const shown = ansi(row.display)
    w.page.drawText(shown, {
      x: PAGE_W - MARGIN - body.widthOfTextAtSize(shown, 9),
      y: w.y,
      size: 9,
      font: body,
      color: rgb(INK.r, INK.g, INK.b),
    })
    w.y -= rowH
  }

  // -- The listing --------------------------------------------------------------
  w.y -= 14
  w.need(60)
  w.text('Invoices Included in This View', MARGIN, 12, bold)
  w.y -= 14
  if (input.invoiceTotal !== null && input.invoiceTotal > input.invoices.length) {
    w.text(`The first ${input.invoices.length} of ${input.invoiceTotal} invoices.`, MARGIN, 9, body, MUTED)
    w.y -= 12
  }
  const cols: { label: string; width: number; right?: boolean; cell: (inv: Invoice) => string }[] = [
    { label: 'Company', width: 92, cell: (inv) => inv.company_name ?? '—' },
    { label: 'Vendor', width: 112, cell: (inv) => inv.vendor_name ?? '—' },
    { label: 'Invoice #', width: 60, cell: (inv) => inv.external_invoice_id ?? '—' },
    { label: 'Invoice Date', width: 56, cell: (inv) => (inv.invoice_date ? formatDate(inv.invoice_date) : '—') },
    { label: 'Uploaded', width: 52, cell: (inv) => formatTimestampDate(inv.uploaded_at) },
    { label: 'Exported', width: 52, cell: (inv) => formatTimestampDate(inv.exported_at) },
    { label: 'Status', width: 52, cell: (inv) => statusLabel(inv.status) },
    { label: 'Total', width: 56, right: true, cell: (inv) => formatCurrency(inv.total) },
  ]
  const listHead = () => {
    let x = MARGIN
    for (const col of cols) {
      const label = fit(col.label, bold, 8, col.width - 4)
      const lx = col.right ? x + col.width - bold.widthOfTextAtSize(label, 8) : x
      w.page.drawText(label, { x: lx, y: w.y, size: 8, font: bold, color: rgb(MUTED.r, MUTED.g, MUTED.b) })
      x += col.width
    }
    w.y -= 5
    w.rule()
    w.y -= 10
  }
  listHead()
  if (input.invoices.length === 0) {
    w.text('No invoices match these filters.', MARGIN, 9, body, MUTED)
    w.y -= 12
  }
  for (const inv of input.invoices) {
    if (w.need(12)) listHead()
    let x = MARGIN
    for (const col of cols) {
      const text = fit(col.cell(inv), body, 8, col.width - 4)
      const tx = col.right ? x + col.width - body.widthOfTextAtSize(text, 8) : x
      w.page.drawText(text, { x: tx, y: w.y, size: 8, font: body, color: rgb(INK.r, INK.g, INK.b) })
      x += col.width
    }
    w.y -= 12
  }

  // -- Page numbers ---------------------------------------------------------------
  const pages = doc.getPages()
  pages.forEach((page, i) => {
    const label = `Page ${i + 1} of ${pages.length}`
    page.drawText(label, {
      x: (PAGE_W - body.widthOfTextAtSize(label, 8)) / 2,
      y: MARGIN - 16,
      size: 8,
      font: body,
      color: rgb(MUTED.r, MUTED.g, MUTED.b),
    })
  })
  return await doc.save()
}

/** `first` followed by every page of `second`, as one file. */
export async function appendPdf(first: Uint8Array, second: Uint8Array): Promise<Uint8Array> {
  const { PDFDocument } = await import('pdf-lib')
  const out = await PDFDocument.load(first)
  const extra = await PDFDocument.load(second)
  const copied = await out.copyPages(extra, extra.getPageIndices())
  for (const page of copied) out.addPage(page)
  return await out.save()
}
